import * as Service from 'types/service';
import { PartyListSections } from 'constants/service/singlePartyList';
import { createSelector } from 'reselect';
import { isGuestListSeparateTabsSelector } from 'selectors/dashboardPreferenceSelectors';

import {
  ReservationRightSidePanelStatus,
  SelectedPartyListTabs,
  ServicePartyDragMode,
} from 'state/service/ui';
import { StoreType } from '@tock/shared/utils/connect';

export const serviceUiSelector = createSelector(
  (state: StoreType) => state.service.ui,
  (ui) => ui
);

export const getPartyListTextFilter = (state: StoreType) => state.service.ui.partyListTextFilter;

export const getSelectedPartyId = (state: StoreType) => state.service.ui.selectedPartyId;

export const getPrevSelectedPartyId = (state: StoreType) => state.service.ui.prevSelectedPartyId;

export const getReservationRightSidePanels = createSelector(
  serviceUiSelector,
  (ui): ReservationRightSidePanelStatus => ui.reservationRightSidePanels
);

export const isAssigningBusinessServerSelector = createSelector(serviceUiSelector, (ui): boolean =>
  ui.selectedBusinessServerState ? ui.selectedBusinessServerState.isAssigning : false
);

export const isEditingWalkInSelector = createSelector(
  serviceUiSelector,
  (ui): boolean => ui.editingWalkIn
);

export const latestExpectedReservationTimeSelector = createSelector(
  serviceUiSelector,
  (ui): string | null => ui.latestExpectedReservationTime
);

export const latestQuotedTimeMinSelector = createSelector(
  serviceUiSelector,
  (ui): string => ui.latestQuotedTimeMin
);

export const creatingWalkInOfTypeSelector = createSelector(
  serviceUiSelector,
  (ui): Service.WalkInType | null => ui.creatingWalkInOfType
);

export const walkInSortSelector = (state: StoreType) => state.service.ui.walkInSortByArrival;

export const selectedTableIdsSelector = createSelector(
  serviceUiSelector,
  (ui): Array<number | Long> | null => ui.selectedTableIds
);

/* istanbul ignore next */
export const newAssigningSessionSelector = createSelector(
  serviceUiSelector,
  (ui): boolean => ui.newAssigningSession
);

/* istanbul ignore next */
export const isAssigningTablesSelector = createSelector(
  serviceUiSelector,
  (ui): boolean => ui.isAssigningTables
);

/* istanbul ignore next */
export const selectedPartyPanelProfileTabSelector = createSelector(
  serviceUiSelector,
  (ui): string => ui.selectedPartyPanelProfileTab
);

/* istanbul ignore next */
export const servicePartyDragModeSelector = createSelector(
  (state: StoreType) => state.service.ui.partyDragMode,
  (partyDragMode: ServicePartyDragMode) => partyDragMode
);

/* istanbul ignore next */
export const selectedPartyListTabsSelector = createSelector(
  (state: StoreType) => state.service.ui.selectedPartyListTabs,
  (selectedPartyListTabs: SelectedPartyListTabs) => selectedPartyListTabs
);
/* istanbul ignore next */
export const selectedGuestListTabSelector = createSelector(
  (state: StoreType) => state.persistedUi.selectedGuestListTab,
  (selectedGuestListTab) => selectedGuestListTab
);

/**
 * Central logic to return the selected guest list section.
 * Focusable guest lists don't always have a selected section.
 * Tabbed guest lists default to reservations.
 */
export const selectedGuestListSectionSelector = createSelector(
  serviceUiSelector,
  isGuestListSeparateTabsSelector,
  (ui, isGuestListTabbed) =>
    isGuestListTabbed && !ui.selectedGuestListSection
      ? PartyListSections.UPCOMING
      : ui.selectedGuestListSection
);

export const seatedWalkInIdSelector = (state: StoreType) => state.service.ui.seatedWalkInId;

export const selectedSidebarTabSelector = (state: StoreType) => state.service.ui.selectedSidebarTab;
